import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthToken, setAuthToken } from '../lib/authToken';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAuthToken());
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setAuthToken(null);
    localStorage.removeItem('userRole');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  // Session expirée : déconnexion propre + message clair
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        const detail = error.response?.data?.detail;
        if (detail && typeof detail !== 'string') {
          error.response.data.detail = Array.isArray(detail)
            ? detail.map(d => `${(d.loc || []).slice(1).join('.') || 'champ'} : ${d.msg}`).join(' ; ')
            : JSON.stringify(detail);
        }
        if (status === 401 && !url.includes('/auth/login')) {
          logout();
          toast.error('Votre session a expiré, veuillez vous reconnecter', { id: 'session-expired' });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [logout]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
      } catch (error) {
        logout();
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [token, logout]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const data = response.data;

    // Multi-profile: return profiles for selection
    if (data.multi_profile) {
      return { multi_profile: true, profiles: data.profiles, temp_token: data.temp_token, user_name: data.user_name };
    }

    // Single profile: direct login
    const { token: newToken, user: userData } = data;
    setAuthToken(newToken);
    localStorage.setItem('userRole', userData.role);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const selectProfile = async (tempToken, profileId) => {
    const response = await axios.post(`${API_URL}/auth/select-profile`, { temp_token: tempToken, profile_id: profileId });
    const { token: newToken, user: userData } = response.data;
    setAuthToken(newToken);
    localStorage.setItem('userRole', userData.role);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    setAuthToken(newToken);
    localStorage.setItem('userRole', newUser.role);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles || (user.role === 'super_admin' && roles === 'admin');
    // super_admin has admin access
    if (user.role === 'super_admin' && roles.includes('admin')) return true;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, selectProfile, register, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
